<template>
  <div
    class="inspection-status-alert"
    :class="`inspection-status-alert--${status}`"
    :role="status === 'failed' ? 'alert' : 'status'"
  >
    <span class="inspection-status-alert__icon">
      <img :src="statusIcon" alt="" class="inspection-status-alert__icon-glyph" />
    </span>

    <div class="inspection-status-alert__copy">
      <p class="inspection-status-alert__title">{{ alertTitle }}</p>
      <p class="inspection-status-alert__description">{{ alertDescription }}</p>
    </div>

    <div
      v-if="showInspectors"
      class="inspection-status-alert__inspectors"
      aria-label="Available inspectors"
    >
      <span>Available Inspectors:</span>
      <PeopleAvatarsButton
        :avatars="inspectors"
        :max-visible="3"
        size="small"
        :is-hover-enable="false"
        :is-show-tooltip="true"
        :allow-drill-down="false"
        @onAvatarClick="emit('inspectors-click')"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import PeopleAvatarsButton from "@components/PeopleAvatarsButton/PeopleAvatarsButton.vue";

const props = defineProps({
  status: {
    type: String,
    default: "pending",
    validator: (value) => ["pending", "passed", "failed", "not_applicable"].includes(value),
  },
  title: { type: String, default: "" },
  description: { type: String, default: "" },
  inspectorName: { type: String, default: "" },
  inspectionDate: { type: String, default: "" },
  inspectors: { type: Array, default: () => [] },
});

const emit = defineEmits(["inspectors-click"]);

const STATUS_ICONS = {
  pending: require("@components/assets/info-circle-brand.svg"),
  passed: require("@components/assets/check-circle-success.svg"),
  failed: require("@components/assets/alert-circle-error.svg"),
  not_applicable: require("@components/assets/info-circle-brand.svg"),
};

const DEFAULT_TITLES = {
  pending:
    "In order to complete this checkpoint a coach must inspect your abilities and consistent behavior",
  passed: "You successfully passed inspection for this checkpoint",
  failed: "You failed inspection for this checkpoint",
  not_applicable: "This checkpoint was marked not applicable",
};

const statusIcon = computed(() => STATUS_ICONS[props.status] || STATUS_ICONS.pending);

// A passed checkpoint needs no further inspection, so the coach list is dropped
const showInspectors = computed(
  () => !["passed", "not_applicable"].includes(props.status) && props.inspectors.length > 0
);

const alertTitle = computed(() => props.title || DEFAULT_TITLES[props.status]);

const verdictAuthor = computed(() =>
  [props.inspectionDate ? `On ${props.inspectionDate}` : "", props.inspectorName]
    .filter(Boolean)
    .join(" ")
);

const alertDescription = computed(() => {
  if (props.description) return props.description;

  if (props.status === "passed") {
    return verdictAuthor.value
      ? `${verdictAuthor.value} confirmed your abilities and consistent behavior for this checkpoint`
      : "Your abilities and consistent behavior for this checkpoint have been confirmed.";
  }

  if (props.status === "failed") {
    return verdictAuthor.value
      ? `${verdictAuthor.value} failed to confirm your abilities and consistent behavior for this checkpoint. Prepare and ask again for inspection`
      : "Your abilities and consistent behavior for this checkpoint were not confirmed. Prepare and ask again for inspection.";
  }

  if (props.status === "not_applicable") {
    return verdictAuthor.value
      ? `${verdictAuthor.value} marked this checkpoint as not applicable`
      : "A coach marked this checkpoint as not applicable.";
  }

  return "Please reach out to a certified coach of this responsibility to schedule an inspection.";
});
</script>

<style scoped>
.inspection-status-alert {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
  color: #344054;
  font-family: Inter, sans-serif;
}

.inspection-status-alert__icon {
  position: relative;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin: 8px;
  color: #7f56d9;
}

.inspection-status-alert--passed .inspection-status-alert__icon {
  color: #079455;
}

.inspection-status-alert--failed .inspection-status-alert__icon {
  color: #d92d20;
}

.inspection-status-alert--not_applicable .inspection-status-alert__icon {
  color: #667085;
}

/* Concentric halo rings around the status icon */
.inspection-status-alert__icon::before,
.inspection-status-alert__icon::after {
  position: absolute;
  border: 2px solid currentColor;
  border-radius: 50%;
  content: "";
}

.inspection-status-alert__icon::before {
  inset: -4px;
  opacity: 0.3;
}

.inspection-status-alert__icon::after {
  inset: -9px;
  opacity: 0.1;
}

.inspection-status-alert__icon-glyph {
  position: absolute;
  top: 8.33%;
  left: 8.33%;
  width: 83.34%;
  height: 83.34%;
}

.inspection-status-alert__copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 32px;
}

.inspection-status-alert__title,
.inspection-status-alert__description {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
}

.inspection-status-alert__title {
  font-weight: 600;
}

.inspection-status-alert__description {
  margin-top: 4px;
  color: #475467;
}

.inspection-status-alert__inspectors {
  display: flex;
  flex: 0 0 auto;
  align-self: center;
  align-items: center;
  gap: 20px;
  color: #101828;
  font-size: 14px;
  font-weight: 500;
}

@media (max-width: 760px) {
  .inspection-status-alert {
    flex-direction: column;
  }

  .inspection-status-alert__copy {
    padding-right: 0;
  }

  .inspection-status-alert__inspectors {
    align-self: flex-start;
  }
}
</style>
